import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileText, Download, X, Loader2 } from "lucide-react";
import api, { formatError } from "../lib/api";
import { SectionHeading, Reveal } from "./ui";

export default function Brochures() {
  const [items, setItems] = useState([]);
  const [gate, setGate] = useState(null); // brochure being gated
  const [form, setForm] = useState({ name: "", phone: "", city: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => { api.get("/brochures").then((r) => setItems(r.data || [])); }, []);
  if (!items.length) return null;

  const open = (url) => { if (url) window.open(url, "_blank", "noopener"); else alert("PDF will be available soon. Please contact us."); };

  const submitGate = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.phone.trim().length < 7) { setError("Please enter your name and a valid mobile number."); return; }
    setStatus("loading"); setError("");
    try {
      const { data } = await api.post("/brochures/download", { brochure_id: gate.id, ...form });
      setStatus("idle"); setGate(null); setForm({ name: "", phone: "", city: "" });
      open(data.pdf_url);
    } catch (e2) { setError(formatError(e2.response?.data?.detail)); setStatus("idle"); }
  };

  const inp = "w-full rounded-xl border border-line bg-white px-4 py-3 text-[15px] outline-none focus:border-gold";

  return (
    <section className="py-16 md:py-20 bg-white" id="brochures">
      <div className="container-mid px-5 md:px-8">
        <Reveal><SectionHeading center kicker="Downloads" title="Brochures & Guides" subtitle="Download our company profile and package details." /></Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {items.map((b) => (
            <Reveal key={b.id}>
              <div className="rounded-xl2 border border-line overflow-hidden bg-white h-full flex flex-col" data-testid={`brochure-${b.id}`}>
                <div className="relative aspect-[16/10] bg-sand">{b.thumbnail && <img src={b.thumbnail} alt={b.title} className="img-cover" referrerPolicy="no-referrer" />}
                  <span className="absolute top-3 left-3 bg-ink/70 text-white text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wide">{b.category}</span>
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <h3 className="font-bold text-ink flex items-center gap-2"><FileText size={16} className="text-gold" /> {b.title}</h3>
                  <p className="text-sm text-muted mt-1.5 flex-1">{b.description}</p>
                  <button onClick={() => (b.gated ? setGate(b) : open(b.pdf_url))} data-testid={`brochure-download-${b.id}`} className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-ink text-white font-semibold py-2.5 hover:bg-black transition-colors">
                    <Download size={15} /> {b.gated ? "Get Download" : "Download PDF"}
                  </button>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {gate && (
          <motion.div className="fixed inset-0 z-[95] bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setGate(null)}>
            <motion.div className="bg-white rounded-xl2 w-full max-w-md p-6" initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ opacity: 0 }} onClick={(e) => e.stopPropagation()} data-testid="brochure-gate">
              <div className="flex justify-between items-start mb-4">
                <div><div className="kicker mb-1">Free Download</div><h3 className="text-lg font-bold text-ink">{gate.title}</h3></div>
                <button onClick={() => setGate(null)} className="w-9 h-9 rounded-full hover:bg-sand flex items-center justify-center"><X size={18} /></button>
              </div>
              <form onSubmit={submitGate} className="space-y-3">
                <input className={inp} placeholder="Full Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="brochure-name" />
                <input className={inp} placeholder="Mobile Number *" type="tel" inputMode="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="brochure-phone" />
                <input className={inp} placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                {error && <p className="text-sm text-red-500">{error}</p>}
                <button type="submit" disabled={status === "loading"} data-testid="brochure-gate-submit" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold text-white font-semibold py-3 shadow-gold disabled:opacity-70">
                  {status === "loading" ? <><Loader2 size={16} className="animate-spin" /> Please wait...</> : "Download Now"}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
